import React, { useEffect, useMemo, useReducer } from 'react';

import { UserDataGroupResponse } from '@ariksa/abac/api';
import { JitDataGroupStatus } from '@ariksa/jit/api';
import { Box, Button, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { colorOpacity, customTheme } from 'theme';

import { formatTooltip, renderTooltipFieldValue } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { Select } from 'components/DataEntry';
import {
  IconTypes,
  renderTableHeaderWithLoader,
  StackedCell,
  Tag,
  WithResourceIcon,
} from 'components/index';
import { renderDataGroupSensitivityLabels } from 'containers/Jit/Components/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { toTitleCase } from 'utils/string';

import { selectDataCatalog } from '../selectors';

import { RequestAccessModal } from './RequestAccessModal';

interface CatalogTableProps {
  searchTerm: string;
  status: Record<string, JitDataGroupStatus>;
  getDataGroups: () => void;
}

interface CatalogTableState {
  sensitivityOptions: Record<string, any>[];
  ownerOptions: Record<string, any>[];
  filteredDataGroups: UserDataGroupResponse[];
  selectedDataGroup: UserDataGroupResponse | null;
  selectedSensitivity?: Record<string, any>;
  selectedOwner?: Record<string, any>;
}

const initialCatalogTableState: CatalogTableState = {
  sensitivityOptions: [{ label: 'Sensitivity', value: undefined }],
  ownerOptions: [{ label: 'Owner', value: undefined }],
  filteredDataGroups: [],
  selectedDataGroup: null,
  selectedSensitivity: undefined,
  selectedOwner: undefined,
};

export const CatalogTable = ({
  searchTerm,
  status,
  getDataGroups,
}: CatalogTableProps) => {
  const { dataGroups, dataGroupSensitivity } = useSelector(selectDataCatalog);
  const navigate = useNavigate();
  const requestAccess = useDisclosure();
  const [state, updateState] = useReducer(
    (
      prev: CatalogTableState,
      next:
        | Partial<CatalogTableState>
        | ((prev: CatalogTableState) => CatalogTableState),
    ) => {
      if (typeof next === 'function') return next(prev);
      return { ...prev, ...next };
    },
    initialCatalogTableState,
  );

  const { renderStackedAccountNameID } = useCloudAccountId();

  const sensitivityLoading = Object.values(dataGroupSensitivity ?? {}).some(
    sensitivity => sensitivity?.isLoading,
  );

  const catalogDataGroups = useMemo(
    () =>
      (dataGroups.data?.data_groups ?? []).map(dataGroup => ({
        ...dataGroup,
        sensitivity_categories:
          dataGroupSensitivity?.[dataGroup.uuid]?.data
            ?.sensitivity_categories ?? [],
      })),
    [dataGroups.data, dataGroupSensitivity],
  );

  useEffect(() => {
    const categories = new Set(
      catalogDataGroups.flatMap(dataGroup => dataGroup?.sensitivity_categories),
    );

    const sensitivityOptions = Array.from(categories)
      .sort()
      .map(category => ({
        label: toTitleCase(category),
        value: category,
      }));
    updateState(prev => ({
      ...prev,
      sensitivityOptions,
      selectedSensitivity:
        sensitivityOptions.find(
          option => option.value === prev.selectedSensitivity?.value,
        ) ?? undefined,
    }));
  }, [catalogDataGroups]);

  useEffect(() => {
    const owners = new Map<string, string>();

    catalogDataGroups.forEach(dataGroup => {
      const owner = dataGroup.owner;
      const ownerValue = owner?.id || owner?.name;

      if (ownerValue && owner?.name) owners.set(ownerValue, owner.name);
    });

    const ownerOptions = Array.from(owners, ([value, label]) => ({
      label,
      value,
    })).sort((first, second) => first.label.localeCompare(second.label));
    updateState({
      ownerOptions,
    });
  }, [catalogDataGroups]);

  useEffect(() => {
    const search = searchTerm.trim().toLowerCase();
    const sensitivity = state.selectedSensitivity?.value;
    const owner = state.selectedOwner?.value;

    const filteredDataGroups = catalogDataGroups.filter(dataGroup => {
      const matchesSearch =
        !search ||
        [dataGroup.name, dataGroup.description, dataGroup.team]
          .filter(Boolean)
          .some(value => value!.toLowerCase().includes(search));
      const matchesSensitivity =
        !sensitivity || dataGroup.sensitivity_categories.includes(sensitivity);
      const matchesOwner =
        !owner ||
        dataGroup.owner?.id === owner ||
        dataGroup.owner?.name === owner;

      return matchesSearch && matchesSensitivity && matchesOwner;
    });
    updateState({ filteredDataGroups });
  }, [
    catalogDataGroups,
    searchTerm,
    state.selectedOwner,
    state.selectedSensitivity,
  ]);

  const data = state.filteredDataGroups;

  const columns = [
    {
      header: (
        <Box pl={10} ml={1}>
          Data Group
        </Box>
      ),
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={IconTypes.DataGroup}
          color={customTheme.colors.blue['300']}
          iconStyles={{ boxSize: 8 }}
        >
          <StackedCell
            upper={row?.name ?? '-'}
            lower={row?.team ?? '-'}
            showUpperTooltip={false}
            showLowerTooltip={false}
            tooltip={formatTooltip({
              header: 'Data Group',
              content: (
                <Stack spacing={1}>
                  {renderTooltipFieldValue('Name', row?.name ?? '-')}
                  {renderTooltipFieldValue('Team', row?.team ?? '-')}
                </Stack>
              ),
              width: 64,
            })}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Description',
      align: 'left',
      render: ({ row }) => row.description || '',
      styles: { maxW: '35%', cell: { w: '35%' } },
    },
    {
      header: 'Source',
      align: 'left',
      render: ({ row }) =>
        row.account?.account_id
          ? renderStackedAccountNameID(row.account?.account_id)
          : '',
    },
    {
      header: renderTableHeaderWithLoader('Sensitivity', sensitivityLoading),
      align: 'left',
      render: ({ row }) =>
        renderDataGroupSensitivityLabels(row.sensitivity_categories),
    },
    {
      header: 'Owner',
      align: 'left',
      render: ({ row }) =>
        row.owner ? (
          <WithResourceIcon
            resourceType={IconTypes.User}
            color={customTheme.colors.blue['300']}
            iconStyles={{ w: 7, h: 7, p: 1 }}
          >
            {row.owner?.name || ''}
          </WithResourceIcon>
        ) : (
          ''
        ),
    },
    {
      header: 'Your Access',
      render: ({ row }) => {
        const isActive = status?.[row?.uuid]?.has_active_request;

        if (isActive) return <StackedCell upper="JIT active" lower={''} />;

        const eligibleAccess = row?.eligible_access?.roles;
        const permanentAccess = row?.permanent_access?.roles;
        const upper = !!permanentAccess?.length ? 'Permanent' : 'JIT eligible';
        const items = !!permanentAccess?.length
          ? permanentAccess
          : eligibleAccess;
        const lower = !!items?.length
          ? items?.length === 1
            ? items?.[0]?.name
            : items?.length + ' roles'
          : '';
        return <StackedCell upper={upper} lower={lower} />;
      },
      align: 'left',
    },
    {
      header: 'Action',
      align: 'left',
      render: ({ row }) => {
        const dataGroupStatus = status?.[row?.uuid];
        const requestStatus = dataGroupStatus?.has_active_request
          ? { label: 'Active', color: customTheme.colors.green['300'] }
          : dataGroupStatus?.has_pending_request
          ? { label: 'Under Review', color: customTheme.colors.yellow['300'] }
          : dataGroupStatus?.has_approved_request
          ? { label: 'Approved', color: customTheme.colors.green['300'] }
          : null;

        return requestStatus ? (
          <Tag
            label={requestStatus.label}
            styles={{
              tag: {
                bg: colorOpacity(requestStatus.color, 0.2),
                borderColor: customTheme.colors.gray['100'],
              },
              label: { color: customTheme.colors.gray['300'] },
            }}
            onClick={() => navigate('/jit/requests')}
          />
        ) : (
          <Button
            size="sm"
            variant="outline"
            fontWeight={400}
            color={customTheme.colors.gray['300']}
            isDisabled={!row.eligible_access?.roles?.length}
            onClick={event => {
              event.stopPropagation();
              updateState({ selectedDataGroup: row });
              requestAccess.onOpen();
            }}
          >
            Request
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <BorderedBox
        flex={1}
        w="full"
        h="full"
        minH={0}
        display="flex"
        flexDirection="column"
        py={4}
        px={6}
      >
        <HStack spacing={2} flexWrap="wrap" mb={4} flexShrink={0}>
          <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
          <Box>
            <Select
              options={state.sensitivityOptions}
              value={state.selectedSensitivity}
              onChange={(option: CatalogFilterOption | null) =>
                updateState({
                  selectedSensitivity: option ?? undefined,
                })
              }
              menuPortalTarget={document.body}
              isAFilter
              leftMessage="Sensitivity"
              showUndefinedValueLabel={false}
              isLoading={sensitivityLoading}
              isDisabled={sensitivityLoading}
            />
          </Box>
          <Box>
            <Select
              options={state.ownerOptions}
              value={state.selectedOwner}
              onChange={option => updateState({ selectedOwner: option })}
              menuPortalTarget={document.body}
              isAFilter
              leftMessage="Owner"
              showUndefinedValueLabel={false}
              isLoading={dataGroups.isLoading}
              isDisabled={dataGroups.isLoading}
            />
          </Box>
        </HStack>
        <Table
          columns={columns}
          data={data}
          isLoading={dataGroups.isLoading}
          isError={dataGroups.isError}
          noDataMessage="No data groups available"
        />
      </BorderedBox>
      {state.selectedDataGroup && (
        <RequestAccessModal
          isOpen={requestAccess.isOpen}
          onClose={() => {
            requestAccess.onClose();
            updateState({ selectedDataGroup: null });
          }}
          getDataGroups={getDataGroups}
          dataGroup={state.selectedDataGroup}
        />
      )}
    </>
  );
};
